import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isStandaloneApp } from '../../utills/helpers';

// In the installed home-screen app, a "new tab" for an in-app page opens a separate
// app window with no history, so Back there closes it and the app seems to quit.
// Open in-app pages in place instead, so Back returns to the page they came from.
// Browser tabs keep opening new tabs as before.

// Same-origin paths that are not app screens (backend, uploaded files, downloads).
const NON_APP_PATH = /^\/(api|storage)\//;
const FILE_PATH = /\.[a-z0-9]+$/i;

export const toInAppPath = (url) => {
  if (url == null || url === '') return null;
  let parsed;
  try {
    parsed = new URL(String(url), window.location.href);
  } catch {
    return null;
  }
  if (parsed.origin !== window.location.origin) return null;
  if (NON_APP_PATH.test(parsed.pathname) || FILE_PATH.test(parsed.pathname)) return null;
  return parsed.pathname + parsed.search + parsed.hash;
};

const useStandaloneInAppLinks = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!isStandaloneApp()) return undefined;

    const originalOpen = window.open;
    window.open = function open(url, target, features) {
      const path = target == null || target === '' || target === '_blank' ? toInAppPath(url) : null;
      if (path) {
        navigateRef.current(path);
        return null;
      }
      return originalOpen.call(window, url, target, features);
    };

    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[target="_blank"]');
      if (!link || link.hasAttribute('download')) return;
      const path = toInAppPath(link.getAttribute('href'));
      if (!path) return;
      event.preventDefault();
      navigateRef.current(path);
    };
    document.addEventListener('click', onClick);

    return () => {
      window.open = originalOpen;
      document.removeEventListener('click', onClick);
    };
  }, []);
};

export default useStandaloneInAppLinks;
